import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import SaveChartButton from "@/components/SaveChartButton";

/**
 * All 20 standard amino acids grouped by side-chain chemistry (the way the
 * MCAT tests). Each row includes: three-letter code, one-letter code, side
 * chain type, whether it's essential (must come from diet), pI (isoelectric
 * point) to 1 decimal, hydrophobic/hydrophilic tag, and a MCAT-focused
 * remember-this note.
 *
 * Sources: standard biochem references; pI values match Lehninger 8th ed.
 */
const AMINO_ACIDS = [
  { name: "Alanine", three: "Ala", one: "A", group: "Nonpolar aliphatic", essential: false, pI: 6.0, tag: "Hydrophobic", note: "Simplest chiral amino acid — CH₃ side chain." },
  { name: "Valine", three: "Val", one: "V", group: "Nonpolar aliphatic", essential: true, pI: 6.0, tag: "Hydrophobic", note: "Branched-chain (BCAA). Isopropyl side chain." },
  { name: "Leucine", three: "Leu", one: "L", group: "Nonpolar aliphatic", essential: true, pI: 6.0, tag: "Hydrophobic", note: "Branched-chain. Most common in proteins." },
  { name: "Isoleucine", three: "Ile", one: "I", group: "Nonpolar aliphatic", essential: true, pI: 6.0, tag: "Hydrophobic", note: "Branched-chain. Two chiral centers." },
  { name: "Methionine", three: "Met", one: "M", group: "Nonpolar aliphatic", essential: true, pI: 5.7, tag: "Hydrophobic", note: "Sulfur-containing (thioether). Start codon codes for Met." },
  { name: "Proline", three: "Pro", one: "P", group: "Nonpolar aliphatic", essential: false, pI: 6.3, tag: "Hydrophobic", note: "Only imino acid — kinks α-helices, forms β-turns." },
  { name: "Phenylalanine", three: "Phe", one: "F", group: "Aromatic", essential: true, pI: 5.5, tag: "Hydrophobic", note: "Aromatic. Absorbs UV weakly at 260 nm." },
  { name: "Tryptophan", three: "Trp", one: "W", group: "Aromatic", essential: true, pI: 5.9, tag: "Hydrophobic", note: "Largest AA. Strong UV absorbance at 280 nm." },
  { name: "Tyrosine", three: "Tyr", one: "Y", group: "Aromatic", essential: false, pI: 5.7, tag: "Polar", note: "Aromatic + ionizable -OH (pKa ≈ 10.5). UV at 280 nm." },
  { name: "Serine", three: "Ser", one: "S", group: "Polar uncharged", essential: false, pI: 5.7, tag: "Polar", note: "-OH group — common phosphorylation site." },
  { name: "Threonine", three: "Thr", one: "T", group: "Polar uncharged", essential: true, pI: 5.6, tag: "Polar", note: "-OH group. Two chiral centers." },
  { name: "Cysteine", three: "Cys", one: "C", group: "Polar uncharged", essential: false, pI: 5.1, tag: "Polar", note: "Thiol -SH forms disulfide bonds (Cys-Cys)." },
  { name: "Asparagine", three: "Asn", one: "N", group: "Polar uncharged", essential: false, pI: 5.4, tag: "Polar", note: "Amide of aspartate. N-linked glycosylation site." },
  { name: "Glutamine", three: "Gln", one: "Q", group: "Polar uncharged", essential: false, pI: 5.7, tag: "Polar", note: "Amide of glutamate. Most abundant free AA in blood." },
  { name: "Glycine", three: "Gly", one: "G", group: "Polar uncharged", essential: false, pI: 6.0, tag: "Neutral", note: "No side chain (H). Only achiral AA. Flexible in structure." },
  { name: "Lysine", three: "Lys", one: "K", group: "Positively charged", essential: true, pI: 9.7, tag: "Basic", note: "+ charged ε-amino group. pKa ≈ 10.5." },
  { name: "Arginine", three: "Arg", one: "R", group: "Positively charged", essential: false, pI: 10.8, tag: "Basic", note: "Guanidinium group. Highest pI. pKa ≈ 12.5." },
  { name: "Histidine", three: "His", one: "H", group: "Positively charged", essential: true, pI: 7.6, tag: "Basic", note: "Imidazole ring. pKa ≈ 6.0 — only AA that ionizes near physiological pH." },
  { name: "Aspartate", three: "Asp", one: "D", group: "Negatively charged", essential: false, pI: 2.8, tag: "Acidic", note: "-COOH side chain. pKa ≈ 3.9." },
  { name: "Glutamate", three: "Glu", one: "E", group: "Negatively charged", essential: false, pI: 3.2, tag: "Acidic", note: "-COOH side chain. pKa ≈ 4.1. Neurotransmitter." },
];

const GROUP_COLORS = {
  "Nonpolar aliphatic": { bg: "#f3ede1", text: "#7a5b2a", pill: "#c98a3b" },
  Aromatic: { bg: "#efe4d8", text: "#6a4a2f", pill: "#a5754a" },
  "Polar uncharged": { bg: "#e8f1e5", text: "#3d5a2f", pill: "#7ba55c" },
  "Positively charged": { bg: "#e5edf5", text: "#2d4a6f", pill: "#4f7d8a" },
  "Negatively charged": { bg: "#f6e5e5", text: "#7a3a3a", pill: "#c95e5e" },
};

/** Full amino-acid reference chart. Mount above the analogy content on the two
 *  Amino Acids topic pages so students always have the periodic-table-style
 *  reference right where they need it.
 */
export default function AminoAcidChart() {
  const [query, setQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");

  const groups = useMemo(() => Object.keys(GROUP_COLORS), []);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return AMINO_ACIDS.filter((a) => {
      if (groupFilter !== "all" && a.group !== groupFilter) return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        a.three.toLowerCase().includes(q) ||
        a.one.toLowerCase() === q ||
        a.group.toLowerCase().includes(q)
      );
    });
  }, [query, groupFilter]);

  return (
    <div className="pm-card p-6 mb-6" data-testid="amino-acid-chart">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="font-display font-bold text-xl">
            The 20 Standard Amino Acids
          </div>
          <p
            className="text-xs mt-1"
            style={{ color: "var(--text-muted)" }}
          >
            Grouped by side-chain chemistry — the way the MCAT tests them. Tap a
            group chip to filter. Essential AAs (must come from diet) are marked
            with a star.
          </p>
        </div>
        <div className="text-xs flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
          <span>{filtered.length}/{AMINO_ACIDS.length} shown</span>
          <SaveChartButton
            sourceChart="amino-acids"
            title="The 20 Standard Amino Acids"
            cards={AMINO_ACIDS.map((a) => ({
              front: `${a.name} (${a.three} · ${a.one})`,
              back: `${a.group}. pI ${a.pI} · ${a.tag}${a.essential ? " · Essential ★" : ""}. ${a.note}`,
              meta: { one: a.one, three: a.three, group: a.group },
            }))}
          />
        </div>
      </div>

      {/* Search + filter chips */}
      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ color: "var(--text-muted)" }}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, 3-letter code, or 1-letter code…"
            className="pm-input pl-9 w-full"
            data-testid="amino-search"
          />
        </div>
        <div
          className="flex flex-wrap gap-2"
          data-testid="amino-group-filter"
        >
          <button
            onClick={() => setGroupFilter("all")}
            className="rounded-full text-xs font-medium px-3 py-1.5 border"
            style={{
              background:
                groupFilter === "all" ? "var(--primary)" : "var(--paper)",
              color:
                groupFilter === "all"
                  ? "white"
                  : "var(--text-secondary)",
              borderColor:
                groupFilter === "all" ? "var(--primary)" : "var(--border)",
            }}
            data-testid="amino-group-all"
          >
            All groups
          </button>
          {groups.map((g) => {
            const active = groupFilter === g;
            const c = GROUP_COLORS[g];
            return (
              <button
                key={g}
                onClick={() => setGroupFilter(g)}
                className="rounded-full text-xs font-medium px-3 py-1.5 border inline-flex items-center gap-1.5"
                style={{
                  background: active ? c.pill : "var(--paper)",
                  color: active ? "white" : c.text,
                  borderColor: active ? c.pill : "var(--border)",
                }}
                data-testid={`amino-group-${g.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <span
                  className="w-2 h-2 rounded-full inline-block"
                  style={{ background: active ? "white" : c.pill }}
                />
                {g}
              </button>
            );
          })}
        </div>
      </div>

      {/* Grid — mini periodic-table-style cards */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2"
        data-testid="amino-grid"
      >
        {filtered.map((a) => {
          const c = GROUP_COLORS[a.group];
          return (
            <div
              key={a.three}
              className="rounded-lg p-3 relative"
              style={{
                background: c.bg,
                borderLeft: `3px solid ${c.pill}`,
              }}
              data-testid={`amino-${a.three.toLowerCase()}`}
              title={a.note}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span
                  className="font-display font-bold text-2xl leading-none"
                  style={{ color: c.pill }}
                >
                  {a.one}
                </span>
                <span
                  className="text-[10px] font-mono"
                  style={{ color: c.text }}
                >
                  {a.three.toUpperCase()}
                </span>
              </div>
              <div
                className="font-semibold text-sm leading-tight flex items-center gap-1"
                style={{ color: c.text }}
              >
                {a.name}
                {a.essential && (
                  <span
                    style={{ color: "#c98a3b" }}
                    title="Essential — must come from diet"
                  >
                    ★
                  </span>
                )}
              </div>
              <div
                className="text-[10px] mt-1"
                style={{ color: c.text, opacity: 0.75 }}
              >
                pI {a.pI} · {a.tag}
              </div>
              <div
                className="text-[10px] mt-1.5 leading-snug"
                style={{ color: c.text }}
              >
                {a.note}
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div
          className="text-center py-6 text-sm"
          style={{ color: "var(--text-muted)" }}
          data-testid="amino-empty"
        >
          No amino acids match that filter.
        </div>
      )}

      {/* Legend + memory hooks */}
      <div
        className="mt-5 pt-4 border-t text-xs grid md:grid-cols-2 gap-3"
        style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
      >
        <div>
          <b>Essential (★):</b> Phe, Val, Thr, Trp, Met, Leu, Ile, Lys, His —
          mnemonic <i>"PVT TIM HALL"</i>. His is conditionally essential in
          adults but always essential in children.
        </div>
        <div>
          <b>pI trick:</b> If side chain is neutral, pI ≈ 6. Acidic AAs sit low
          (~3), basic AAs sit high (~9–11). His pI (7.6) is the closest to
          physiological pH — why it's a great buffer in hemoglobin.
        </div>
      </div>
    </div>
  );
}
